import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft, Copy, RefreshCw, CheckCircle2, AlertTriangle, Eye, EyeOff, Loader2,
  Save, QrCode, ExternalLink, ChevronLeft, ChevronRight, Plus, Trash2, Layers, ShoppingBag, Star,
  Upload, Palette, Bell, Pen
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import CardDesigner from "@/components/CardDesigner";
import { API } from "@/lib/api";
import { DEFAULT_BRAND_COLOR, DEFAULT_ON_BRAND, brand } from "@/lib/brand";


const LOGIC_META = {
  spend_based: { label: "Points for Money Spent", icon: Star },
  order_based: { label: "Stamp Card (per Order)", icon: ShoppingBag },
  tier_based: { label: "Tiered Rewards", icon: Layers },
  item_based: { label: "Item-based", icon: Star },
};
const ENDPOINT_COLORS = {
  "collect-points": "bg-purple-50 text-purple-700 border-purple-200",
  redeem: "bg-purple-100 text-success border-success/30",
  onboard: "bg-purple-100 text-purple-700 border-purple-200",
};
const DEFAULT_TIERS = [
  { name: "Bronze", min_points: 0, reward_value_gbp: 0, perks: "" },
  { name: "Silver", min_points: 500, reward_value_gbp: 5, perks: "" },
  { name: "Gold", min_points: 1500, reward_value_gbp: 15, perks: "" },
];

function tiersFromConfig(config) {
  if (!config || config.length === 0) return DEFAULT_TIERS.map(t => ({ ...t }));
  return config.map(t => ({ ...t, perks: Array.isArray(t.perks) ? t.perks.join(", ") : (t.perks || "") }));
}

export default function VendorDetailPage() {
  const { id } = useParams();
  const { authHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [apiLogs, setApiLogs] = useState([]);
  const [apiLogsTotal, setApiLogsTotal] = useState(0);
  const [apiLogsPage, setApiLogsPage] = useState(1);
  const [apiLogsLoaded, setApiLogsLoaded] = useState(false);
  const [apiLogsLoading, setApiLogsLoading] = useState(false);
  const [valueCheck, setValueCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedPortal, setCopiedPortal] = useState(false);
  const [copiedClientId, setCopiedClientId] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editClientId, setEditClientId] = useState("");
  const [clientIdSaving, setClientIdSaving] = useState(false);
  const [clientIdError, setClientIdError] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [tiers, setTiers] = useState(DEFAULT_TIERS.map(t => ({ ...t })));
  const [stampRewards, setStampRewards] = useState([]);
  const [logoUploading, setLogoUploading] = useState(null);
  const [brandColor, setBrandColor] = useState(DEFAULT_BRAND_COLOR);
  const [textColor, setTextColor] = useState(DEFAULT_ON_BRAND);
  const [bodyTextColor, setBodyTextColor] = useState(brand.color.line);
  const [cardLayout, setCardLayout] = useState(null);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [msgTitle, setMsgTitle] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [msgResult, setMsgResult] = useState(null);
  const [activeToggling, setActiveToggling] = useState(false);

  const portalUrl = vendor?.slug ? `${window.location.origin}/${vendor.slug}` : `${window.location.origin}/portal?client=${id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=8&data=${encodeURIComponent(portalUrl)}`;

  const load = async () => {
    const h = authHeaders();
    const [v, cu, tx, vc] = await Promise.all([
      axios.get(`${API}/vendors/${id}`, { headers: h }),
      axios.get(`${API}/customers?client_id=${id}&limit=10`, { headers: h }),
      axios.get(`${API}/transactions?client_id=${id}&limit=10`, { headers: h }),
      axios.get(`${API}/vendors/${id}/value-check`, { headers: h }),
    ]);
    setVendor(v.data);
    setEditForm(v.data);
    setEditClientId(v.data.custom_client_id || "");
    setTiers(tiersFromConfig(v.data.tier_config));
    setBrandColor(v.data.brand_color || DEFAULT_BRAND_COLOR);
    setTextColor(v.data.text_color || DEFAULT_ON_BRAND);
    setBodyTextColor(v.data.body_text_color || brand.color.line);
    setCardLayout(v.data.card_layout || null);
    setStampRewards(v.data.stamp_rewards?.length > 0
      ? v.data.stamp_rewards.map(s => ({ ...s }))
      : [{ orders_required: 5, reward_type: "item", description: "", value_gbp: 0, discount_percent: 0 },
         { orders_required: 10, reward_type: "money", description: "", value_gbp: 5, discount_percent: 0 }]
    );
    setCustomers(cu.data.customers || []);
    setTransactions(tx.data.transactions || []);
    setValueCheck(vc.data);
    setLoading(false);
  };

  const loadApiLogs = useCallback(async (page = 1) => {
    setApiLogsLoading(true);
    try {
      const { data } = await axios.get(`${API}/vendors/${id}/api-logs?page=${page}&limit=20`, { headers: authHeaders() });
      setApiLogs(data.logs || []);
      setApiLogsTotal(data.total || 0);
      setApiLogsPage(page);
      setApiLogsLoaded(true);
    } finally {
      setApiLogsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [id]);

  const handleTabChange = (val) => {
    if (val === "api-logs" && !apiLogsLoaded) loadApiLogs(1);
  };

  const handleCopy = () => {
    if (!vendor?.api_key) return;
    navigator.clipboard.writeText(vendor.api_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCopyPortal = () => { navigator.clipboard.writeText(portalUrl); setCopiedPortal(true); setTimeout(() => setCopiedPortal(false), 2000); };
  const handleCopyClientId = () => { navigator.clipboard.writeText(vendor.custom_client_id || id); setCopiedClientId(true); setTimeout(() => setCopiedClientId(false), 2000); };

  const handleSaveClientId = async () => {
    setClientIdSaving(true);
    setClientIdError("");
    try {
      const { data } = await axios.put(`${API}/vendors/${id}/client-id`, { custom_client_id: editClientId }, { headers: authHeaders() });
      setVendor(data);
      setEditClientId(data.custom_client_id || "");
    } catch (err) {
      setClientIdError(err.response?.data?.detail || "Failed to update");
    } finally {
      setClientIdSaving(false);
    }
  };

  const handleToggleActive = async (next) => {
    if (!isAdmin || !vendor) return;
    if (!next) {
      const ok = window.confirm(
        `Disable "${vendor.name}"? They (and their staff) will be unable to log in, use the API, or open the customer portal.`,
      );
      if (!ok) return;
    }
    setActiveToggling(true);
    try {
      const { data } = await axios.put(
        `${API}/vendors/${id}`,
        { is_active: next },
        { headers: authHeaders() },
      );
      setVendor(data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update vendor status");
    } finally {
      setActiveToggling(false);
    }
  };

  const handleRegen = async () => {
    if (!window.confirm("Regenerate API key? Existing integrations will break. The new key is shown only once.")) return;
    setRegenLoading(true);
    const { data } = await axios.post(`${API}/vendors/${id}/regenerate-key`, {}, { headers: authHeaders() });
    setVendor((v) => ({
      ...v,
      api_key: data.api_key,
      api_key_prefix: data.api_key_prefix,
      api_key_last4: data.api_key_last4,
      has_api_key: true,
    }));
    setShowKey(true);
    setRegenLoading(false);
  };

  const handlePushAll = async () => {
    setPushLoading(true);
    setPushResult(null);
    try {
      const { data } = await axios.post(`${API}/vendors/${id}/push-all`, {}, { headers: authHeaders() });
      setPushResult(data);
    } catch (err) {
      setPushResult({ error: err.response?.data?.detail || "Failed to send push notifications" });
    } finally {
      setPushLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!msgTitle.trim() || !msgBody.trim()) return;
    setMsgSending(true);
    setMsgResult(null);
    try {
      const { data } = await axios.post(`${API}/push/send/${id}`, {
        title: msgTitle.trim(),
        body: msgBody.trim(),
        url: portalUrl,
      }, { headers: authHeaders() });
      setMsgResult(data);
      setMsgTitle("");
      setMsgBody("");
    } catch (err) {
      setMsgResult({ error: err.response?.data?.detail || "Failed to send notification" });
    } finally {
      setMsgSending(false);
    }
  };

  const handleBrandingSave = async () => {
    setBrandingSaving(true);
    try {
      await axios.put(`${API}/vendors/${id}`, {
        brand_color: brandColor,
        text_color: textColor,
        body_text_color: bodyTextColor,
        card_layout: cardLayout,
      }, { headers: authHeaders() });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save branding");
    } finally {
      setBrandingSaving(false);
    }
  };


  const addTier = () => setTiers([...tiers, { name: "", min_points: 0, reward_value_gbp: 0, perks: "" }]);
  const removeTier = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const updateTier = (i, field, value) => {
    const updated = [...tiers];
    updated[i] = { ...updated[i], [field]: value };
    setTiers(updated);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const tierPayload = tiers.map(t => ({
      name: t.name,
      min_points: parseInt(t.min_points) || 0,
      reward_value_gbp: parseFloat(t.reward_value_gbp) || 0,
      perks: t.perks ? t.perks.split(",").map(p => p.trim()).filter(Boolean) : [],
    }));
    const updates = {
      name: editForm.name, industry: editForm.industry,
      reward_logic: editForm.reward_logic,
      points_per_pound: parseFloat(editForm.points_per_pound) || 10,
      reward_threshold: parseInt(editForm.reward_threshold) || 500,
      reward_value_gbp: parseFloat(editForm.reward_value_gbp) || 5,
      reward_expiry_days: parseInt(editForm.reward_expiry_days) || 30,
      orders_per_reward: parseInt(editForm.orders_per_reward) || 10,
      stamp_rewards: editForm.reward_logic === "order_based" ? stampRewards.map(s => ({
        orders_required: parseInt(s.orders_required) || 1,
        reward_type: s.reward_type || "money",
        description: s.description || "",
        value_gbp: parseFloat(s.value_gbp) || 0,
        discount_percent: parseFloat(s.discount_percent) || 0,
      })) : [],
      tier_config: editForm.reward_logic === "tier_based" ? tierPayload : [],
      brand_color: brandColor,
      text_color: textColor,
      body_text_color: bodyTextColor,
      card_layout: cardLayout,
    };
    await axios.put(`${API}/vendors/${id}`, updates, { headers: authHeaders() });
    await load();
    setSaving(false);
  };

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(logoType);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/vendors/${id}/upload-logo?logo_type=${logoType}`, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setLogoUploading(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-ink/40">Loading...</div>;
  if (!vendor) return <div className="text-danger">Vendor not found</div>;

  const apiLogsTotalPages = Math.ceil(apiLogsTotal / 20);
  const logic = LOGIC_META[vendor.reward_logic] || LOGIC_META.spend_based;
  const LogicIcon = logic.icon;

  return (
    <div className="space-y-5 max-w-5xl" data-testid="vendor-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(isAdmin ? "/vendors" : "/dashboard")} className="text-ink/50 gap-1.5 -ml-2">
          <ArrowLeft size={14} /> Back
        </Button>
        <div className="h-4 w-px bg-line" />
        <div>
          <h1 className="text-xl font-bold text-ink">{vendor.name}</h1>
          <p className="text-xs text-ink/50">{vendor.industry} · {vendor.customer_count} customers</p>
        </div>
        <Badge variant="outline" className="ml-1 gap-1 text-xs"><LogicIcon size={10} /> {logic.label}</Badge>
        <Badge className={vendor.is_active ? "ml-1 bg-purple-100 text-success border-success/30" : "ml-1 bg-danger/10 text-danger border-danger/30"} variant="outline">
          {vendor.is_active ? "Active" : "Inactive"}
        </Badge>
        {isAdmin && (
          <div className="ml-auto flex items-center gap-2" data-testid="vendor-active-toggle">
            {activeToggling ? <Loader2 size={14} className="animate-spin text-quiet" /> : null}
            <span className="text-xs text-quiet">{vendor.is_active ? "Enabled" : "Disabled"}</span>
            <Switch
              checked={!!vendor.is_active}
              onCheckedChange={handleToggleActive}
              disabled={activeToggling}
              data-testid="vendor-active-switch"
            />
          </div>
        )}
      </div>

      {isAdmin && !vendor.is_active && (
        <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger" data-testid="vendor-disabled-notice">
          This vendor is disabled. Login, staff access, loyalty API, and the customer portal are blocked until re-enabled.
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 pt-4 px-5"><CardTitle className="text-sm font-semibold">Client ID</CardTitle></CardHeader>
          <CardContent className="px-5 pb-4 space-y-2">
            <p className="text-xs text-ink/50">Custom ID used as <code className="bg-purple-50 px-1 rounded">client_id</code> in API calls.</p>
            <div className="flex items-center gap-1.5">
              <Input
                value={editClientId}
                onChange={(e) => { setEditClientId(e.target.value); setClientIdError(""); }}
                placeholder="e.g. my-coffee-shop"
                className="flex-1 h-9 text-xs font-mono"
                data-testid="client-id-input"
              />
              <button onClick={handleCopyClientId} className="p-2 text-ink/40 hover:text-ink/80 rounded-md hover:bg-purple-50 shrink-0" data-testid="copy-client-id">
                <Copy size={14} />
              </button>
            </div>
            {clientIdError && <p className="text-xs text-danger">{clientIdError}</p>}
            {copiedClientId && <p className="text-xs text-ink">Copied!</p>}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSaveClientId} disabled={clientIdSaving} data-testid="save-client-id" className="h-7 text-xs gap-1">
                {clientIdSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
              </Button>
              {!editClientId && <span className="text-[10px] text-ink/40">Fallback: <code className="bg-purple-50 px-1 rounded">{id?.slice(0,12)}...</code></span>}
            </div>
            <p className="text-xs text-ink/40">3-64 chars, letters, numbers, hyphens, underscores</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 pt-4 px-5"><CardTitle className="text-sm font-semibold">API Key</CardTitle></CardHeader>
          <CardContent className="px-5 pb-4 space-y-2">
            <div className="flex items-center gap-1.5">
              <code className="flex-1 min-w-0 px-3 py-2 bg-surface border border-ink/10 rounded-md text-xs font-mono text-ink/80 truncate" data-testid="api-key-display">
                {vendor.api_key
                  ? (showKey ? vendor.api_key : "cl_" + "\u2022".repeat(36))
                  : (vendor.api_key_prefix ? `${vendor.api_key_prefix}…${vendor.api_key_last4 || ""}` : "No key on file")}
              </code>
              {vendor.api_key ? (
                <button onClick={() => setShowKey(v => !v)} className="p-2 text-ink/40 hover:text-ink/80 rounded-md hover:bg-purple-50" data-testid="toggle-api-key">
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              ) : null}
              <button onClick={handleCopy} disabled={!vendor.api_key} className="p-2 text-ink/40 hover:text-ink/80 rounded-md hover:bg-purple-50 disabled:opacity-40" data-testid="copy-api-key">
                <Copy size={14} />
              </button>
            </div>
            {vendor.api_key && <p className="text-xs text-warning">Copy this key now — it will not be shown again after you leave this page.</p>}
            {copied && <p className="text-xs text-ink">Copied!</p>}
            <Button variant="outline" size="sm" onClick={handleRegen} disabled={regenLoading} data-testid="regenerate-api-key" className="h-7 text-xs gap-1">
              {regenLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regenerate Key
            </Button>
            <p className="text-xs text-ink/40">Header: <code className="bg-purple-50 px-1 rounded">X-API-Key: {vendor.api_key_prefix || vendor.api_key?.slice(0, 12) || "cl_…"}...</code></p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><QrCode size={14} className="text-ink/50" /> Customer Portal</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="flex items-start gap-4">
              <img src={qrUrl} alt="Portal QR" className="w-20 h-20 rounded-md border border-ink/10 flex-shrink-0" data-testid="portal-qr-code" />
              <div className="flex-1 min-w-0 space-y-2">
                <p className="text-xs text-ink/50">Customers scan to check points, tier & rewards.</p>
                <div className="flex items-center gap-1.5">
                  <code className="flex-1 min-w-0 px-2 py-1.5 bg-surface border border-ink/10 rounded text-xs font-mono text-ink/60 truncate">{portalUrl}</code>
                  <button onClick={handleCopyPortal} className="p-1.5 text-ink/40 hover:text-ink/80 rounded hover:bg-purple-50" data-testid="copy-portal-url"><Copy size={13} /></button>
                </div>
                {copiedPortal && <p className="text-xs text-ink">URL copied!</p>}
                <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-ink hover:text-purple-700" data-testid="open-portal-link">
                  <ExternalLink size={11} /> Open Portal
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl" data-testid="push-notifications-card">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Bell size={14} className="text-ink/50" /> Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          {/* Send Custom Message */}
          <div>
            <p className="text-xs font-semibold text-ink/60 uppercase tracking-widest mb-2">Send Message to All Customers</p>
            <p className="text-xs text-ink/50 mb-2">Reaches Apple Wallet users (pass refresh) and web push subscribers. The title appears as the main notification text on Apple Wallet.</p>
            <form onSubmit={handleSendMessage} className="space-y-2">
              <Input
                placeholder="Notification title, e.g. Double Stamps Weekend!"
                value={msgTitle}
                onChange={(e) => setMsgTitle(e.target.value)}
                className="h-8 text-sm"
                data-testid="push-msg-title"
                required
              />
              <Input
                placeholder="Message body, e.g. Earn 2x stamps on all orders this Saturday..."
                value={msgBody}
                onChange={(e) => setMsgBody(e.target.value)}
                className="h-8 text-sm"
                data-testid="push-msg-body"
                required
              />
              <div className="flex items-center gap-3">
                <Button
                  type="submit"
                  size="sm"
                  disabled={msgSending || !msgTitle.trim() || !msgBody.trim()}
                  data-testid="send-message-btn"
                  className="h-8 text-xs gap-1.5"
                >
                  {msgSending ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
                  {msgSending ? "Sending..." : "Send Notification"}
                </Button>
                {msgResult && !msgResult.error && (
                  <span className="text-xs text-ink" data-testid="msg-result">
                    Sent to {msgResult.sent} of {msgResult.total_devices} device(s)
                    {msgResult.apns?.devices > 0 && (
                      <span className="text-ink/40"> ({msgResult.apns.sent} Apple Wallet, {msgResult.fcm?.sent || 0} Web Push)</span>
                    )}
                  </span>
                )}
                {msgResult?.error && (
                  <span className="text-xs text-danger" data-testid="msg-error">{msgResult.error}</span>
                )}
              </div>
            </form>
          </div>

          {/* Apple Wallet Refresh */}
          <div className="border-t border-ink/5 pt-3">
            <p className="text-xs font-semibold text-ink/60 uppercase tracking-widest mb-2">Apple Wallet Refresh</p>
            <p className="text-xs text-ink/50 mb-2">
              Force-refresh all Apple Wallet passes. Passes auto-refresh when stamps/rewards change.
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePushAll}
                disabled={pushLoading}
                data-testid="push-all-btn"
                className="h-8 text-xs gap-1.5"
              >
                {pushLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                {pushLoading ? "Sending..." : "Refresh All Passes"}
              </Button>
              {pushResult && !pushResult.error && (
                <span className="text-xs text-ink/60" data-testid="push-result">
                  <span className="font-medium text-ink">{pushResult.sent}</span> sent
                  {" / "}
                  <span className="font-medium">{pushResult.registered_devices}</span> devices
                  {" / "}
                  <span className="font-medium">{pushResult.total}</span> total customers
                </span>
              )}
              {pushResult?.error && (
                <span className="text-xs text-danger" data-testid="push-error">{pushResult.error}</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {valueCheck && valueCheck.status !== "N/A - Item-based logic requires manual review" && (
        <Card className={`border shadow-sm rounded-2xl ${valueCheck.is_meaningful ? "bg-purple-100 border-success/30" : "bg-purple-50 border-warning/40"}`} data-testid="value-validator">
          <CardContent className="px-5 py-3 flex items-start gap-3">
            {valueCheck.is_meaningful
              ? <CheckCircle2 size={16} className="text-ink mt-0.5 flex-shrink-0" />
              : <AlertTriangle size={16} className="text-ink mt-0.5 flex-shrink-0" />}
            <div>
              <p className={`text-sm font-semibold ${valueCheck.is_meaningful ? "text-success" : "text-ink"}`}>Value Validator: {valueCheck.status}</p>
              <p className="text-xs text-ink/60 mt-0.5">
                Customers spend <strong>£{valueCheck.required_spend_gbp}</strong> to earn a <strong>£{valueCheck.reward_value_gbp}</strong> reward ({valueCheck.ratio_percent}% return).
                {!valueCheck.is_meaningful && " Increase reward value to at least 10% of required spend."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="settings" onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customers ({vendor.customer_count})</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Recent Transactions</TabsTrigger>
          <TabsTrigger value="api-logs" data-testid="tab-api-logs">API Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-4">
          <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
            <CardContent className="px-5 py-5">
              <form onSubmit={handleSave} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest">Business Name</Label>
                    <Input value={editForm?.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-vendor-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest">Industry</Label>
                    <Input value={editForm?.industry || ""} onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })} data-testid="edit-vendor-industry" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest">Reward System</Label>
                  <Select value={editForm?.reward_logic || "spend_based"} onValueChange={(v) => {
                    setEditForm({ ...editForm, reward_logic: v });
                    if (v === "tier_based" && tiers.length === 0) setTiers(DEFAULT_TIERS.map(t => ({ ...t })));
                  }}>
                    <SelectTrigger data-testid="edit-reward-logic"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spend_based">Points for Money Spent</SelectItem>
                      <SelectItem value="order_based">Stamp Card (per Order)</SelectItem>
                      <SelectItem value="tier_based">Tiered Rewards</SelectItem>
                      <SelectItem value="item_based">Item-based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(editForm?.reward_logic === "spend_based" || editForm?.reward_logic === "item_based") && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold uppercase tracking-widest">Points per £1</Label>
                      <Input type="number" value={editForm?.points_per_pound || 10} onChange={(e) => setEditForm({ ...editForm, points_per_pound: e.target.value })} data-testid="edit-points-per-pound" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold uppercase tracking-widest">Threshold (pts)</Label>
                      <Input type="number" value={editForm?.reward_threshold || 500} onChange={(e) => setEditForm({ ...editForm, reward_threshold: e.target.value })} data-testid="edit-threshold" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold uppercase tracking-widest">Reward Value (£)</Label>
                      <Input type="number" step="0.01" value={editForm?.reward_value_gbp || 5} onChange={(e) => setEditForm({ ...editForm, reward_value_gbp: e.target.value })} data-testid="edit-reward-value" />
                    </div>
                  </div>
                )}

                {editForm?.reward_logic === "order_based" && (
                  <div className="p-4 bg-purple-100 border border-purple-200 rounded-md space-y-4">
                    <div className="flex items-center gap-2">
                      <ShoppingBag size={14} className="text-ink" />
                      <p className="text-sm font-semibold text-purple-800">Stamp Card Milestones</p>
                      <p className="text-xs text-purple-500">— stamps reset after the highest milestone</p>
                    </div>
                    <div className="space-y-2">
                      <div className="grid grid-cols-12 gap-2 px-1 mb-1">
                        <p className="col-span-2 text-xs font-semibold text-ink/50 uppercase">Stamps</p>
                        <p className="col-span-3 text-xs font-semibold text-ink/50 uppercase">Reward Type</p>
                        <p className="col-span-4 text-xs font-semibold text-ink/50 uppercase">Description</p>
                        <p className="col-span-2 text-xs font-semibold text-ink/50 uppercase">Value</p>
                        <p className="col-span-1" />
                      </div>
                      {stampRewards.map((sr, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center p-3 bg-white border border-purple-200 rounded-md" data-testid={`stamp-reward-row-${i}`}>
                          <Input className="col-span-2 h-8 text-sm" type="number" min="1" placeholder="5" value={sr.orders_required}
                            onChange={(e) => { const u = [...stampRewards]; u[i] = {...u[i], orders_required: e.target.value}; setStampRewards(u); }} />
                          <Select value={sr.reward_type || "money"} onValueChange={(v) => { const u = [...stampRewards]; u[i] = {...u[i], reward_type: v}; setStampRewards(u); }}>
                            <SelectTrigger className="col-span-3 h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="money">Cash (£)</SelectItem>
                              <SelectItem value="item">Free Item</SelectItem>
                              <SelectItem value="percent_discount">% Discount</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input className="col-span-4 h-8 text-sm" placeholder="e.g. Free Coffee, 20% off" value={sr.description}
                            onChange={(e) => { const u = [...stampRewards]; u[i] = {...u[i], description: e.target.value}; setStampRewards(u); }} />
                          <div className="col-span-2">
                            {sr.reward_type === "percent_discount" ? (
                              <Input className="h-8 text-sm" type="number" min="1" max="100" placeholder="%" value={sr.discount_percent}
                                onChange={(e) => { const u = [...stampRewards]; u[i] = {...u[i], discount_percent: e.target.value}; setStampRewards(u); }} />
                            ) : sr.reward_type === "money" ? (
                              <Input className="h-8 text-sm" type="number" min="0" step="0.01" placeholder="£" value={sr.value_gbp}
                                onChange={(e) => { const u = [...stampRewards]; u[i] = {...u[i], value_gbp: e.target.value}; setStampRewards(u); }} />
                            ) : (
                              <span className="text-xs text-ink/40 px-2">N/A</span>
                            )}
                          </div>
                          <button type="button" onClick={() => setStampRewards(stampRewards.filter((_, idx) => idx !== i))} disabled={stampRewards.length <= 1}
                            className="col-span-1 p-1.5 text-ink/40 hover:text-danger disabled:opacity-30 rounded">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm"
                        onClick={() => setStampRewards([...stampRewards, { orders_required: "", reward_type: "money", description: "", value_gbp: 0, discount_percent: 0 }])}
                        className="h-7 text-xs gap-1 mt-1" data-testid="add-stamp-reward">
                        <Plus size={12} /> Add Milestone
                      </Button>
                    </div>
                  </div>
                )}

                {editForm?.reward_logic === "tier_based" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Layers size={14} className="text-ink" />
                      <p className="text-sm font-semibold text-ink">Tier Configuration</p>
                      <p className="text-xs text-ink/40 ml-1">Based on lifetime points earned</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold uppercase tracking-widest">Points earned per £1 spent</Label>
                      <Input type="number" min="1" value={editForm?.points_per_pound || 10} onChange={(e) => setEditForm({ ...editForm, points_per_pound: e.target.value })} className="max-w-xs" data-testid="edit-points-per-pound" />
                    </div>
                    <div className="space-y-2">
                      <div className="grid grid-cols-12 gap-2 px-1 mb-1">
                        <p className="col-span-3 text-xs font-semibold text-ink/50 uppercase">Tier Name</p>
                        <p className="col-span-2 text-xs font-semibold text-ink/50 uppercase">Min Points</p>
                        <p className="col-span-2 text-xs font-semibold text-ink/50 uppercase">Reward (£)</p>
                        <p className="col-span-4 text-xs font-semibold text-ink/50 uppercase">Perks (comma-separated)</p>
                        <p className="col-span-1" />
                      </div>
                      {tiers.map((tier, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center p-3 bg-surface border border-ink/10 rounded-md" data-testid={`tier-row-${i}`}>
                          <Input className="col-span-3 h-8 text-sm" placeholder="Bronze" value={tier.name} onChange={(e) => updateTier(i, "name", e.target.value)} />
                          <Input className="col-span-2 h-8 text-sm" type="number" min="0" value={tier.min_points} onChange={(e) => updateTier(i, "min_points", e.target.value)} />
                          <Input className="col-span-2 h-8 text-sm" type="number" step="0.01" min="0" value={tier.reward_value_gbp} onChange={(e) => updateTier(i, "reward_value_gbp", e.target.value)} />
                          <Input className="col-span-4 h-8 text-sm" placeholder="10% off, free delivery" value={tier.perks} onChange={(e) => updateTier(i, "perks", e.target.value)} />
                          <button type="button" onClick={() => removeTier(i)} disabled={tiers.length <= 1} className="col-span-1 p-1.5 text-ink/40 hover:text-danger disabled:opacity-30 rounded">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={addTier} className="h-7 text-xs gap-1 mt-1" data-testid="add-tier-button">
                        <Plus size={12} /> Add Tier
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest">Reward Expiry (days)</Label>
                  <Input type="number" value={editForm?.reward_expiry_days || 30} onChange={(e) => setEditForm({ ...editForm, reward_expiry_days: e.target.value })} className="max-w-xs" />
                </div>

                <Button type="submit" disabled={saving} className="bg-purple-500 hover:bg-purple-600 text-white gap-1.5" data-testid="save-vendor-settings">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Changes
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="branding" className="mt-4" data-testid="branding-tab-content">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Design Card */}
            <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
              <CardHeader className="pb-3 pt-5 px-6">
                <CardTitle className="text-base font-semibold text-ink">Design</CardTitle>
                <p className="text-xs text-ink/50 mt-0.5">Customize the look and feel of your loyalty card</p>
              </CardHeader>
              <CardContent className="px-6 pb-6 space-y-5">
                {/* Icon Upload */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-ink/80">Icon</Label>
                  <div className="flex items-center gap-4">
                    {vendor.logo_icon_path ? (
                      <img src={`${API}/vendors/${id}/logo/icon`} alt="Icon" className="h-14 w-14 rounded-xl border border-ink/10 object-cover bg-white" data-testid="logo-icon-preview" />
                    ) : (
                      <div className="h-14 w-14 rounded-xl border-2 border-dashed border-ink/15 flex items-center justify-center bg-surface">
                        <Upload size={16} className="text-ink/40" />
                      </div>
                    )}
                    <label className="flex-1 cursor-pointer" data-testid="upload-logo-icon">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e, "icon")} />
                      <div className="h-14 rounded-xl border-2 border-dashed border-ink/10 hover:border-purple-300 transition-colors flex items-center justify-center gap-2 text-xs text-ink/50 bg-surface/50">
                        {logoUploading === "icon" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {logoUploading === "icon" ? "Uploading..." : "Click to upload icon"}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Banner Upload */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-ink/80">Banner image</Label>
                  <label className="block cursor-pointer" data-testid="upload-logo-header">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e, "header")} />
                    {vendor.logo_header_path ? (
                      <div className="relative group">
                        <img src={`${API}/vendors/${id}/logo/header`} alt="Banner" className="w-full h-32 rounded-xl border border-ink/10 object-cover" data-testid="logo-header-preview" />
                        <div className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-medium gap-1">
                          <Upload size={14} /> Replace banner
                        </div>
                      </div>
                    ) : (
                      <div className="h-32 rounded-xl border-2 border-dashed border-ink/10 hover:border-purple-300 transition-colors flex flex-col items-center justify-center gap-1.5 text-ink/50 bg-surface/50">
                        {logoUploading === "header" ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                        <span className="text-xs">{logoUploading === "header" ? "Uploading..." : "Click to upload banner"}</span>
                      </div>
                    )}
                  </label>
                </div>

                {/* Colors */}
                <div className="space-y-3">
                  <Label className="text-xs font-semibold text-ink/80">Colors</Label>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-3">
                      <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)}
                        className="w-8 h-8 rounded-lg border border-ink/10 cursor-pointer p-0.5 shrink-0" data-testid="brand-color-picker" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-ink/80">Background</p>
                        <Input value={brandColor} onChange={(e) => setBrandColor(e.target.value)}
                          className="h-7 text-xs font-mono mt-1" data-testid="brand-color-input" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)}
                        className="w-8 h-8 rounded-lg border border-ink/10 cursor-pointer p-0.5 shrink-0" data-testid="text-color-picker" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-ink/80">Header text</p>
                        <Input value={textColor} onChange={(e) => setTextColor(e.target.value)}
                          className="h-7 text-xs font-mono mt-1" data-testid="text-color-input" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="color" value={bodyTextColor} onChange={(e) => setBodyTextColor(e.target.value)}
                        className="w-8 h-8 rounded-lg border border-ink/10 cursor-pointer p-0.5 shrink-0" data-testid="body-text-color-picker" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-ink/80">Body text</p>
                        <Input value={bodyTextColor} onChange={(e) => setBodyTextColor(e.target.value)}
                          className="h-7 text-xs font-mono mt-1" data-testid="body-text-color-input" />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Content Card */}
            <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
              <CardHeader className="pb-3 pt-5 px-6">
                <CardTitle className="text-base font-semibold text-ink">Content</CardTitle>
                <p className="text-xs text-ink/50 mt-0.5">Configure the pass template details</p>
              </CardHeader>
              <CardContent className="px-6 pb-6 space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-ink/80">Template name</Label>
                  <Input value={vendor.name || ""} disabled className="h-9 text-sm bg-surface" data-testid="template-name" />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-ink/80">Header field</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-ink/40 mb-1">Label</p>
                      <Input value={vendor.reward_logic === "order_based" ? "Stamps" : "Points"} disabled className="h-8 text-xs bg-surface" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-ink/40 mb-1">Value</p>
                      <Input value={vendor.reward_logic === "order_based" ? `0/${vendor.stamp_rewards?.length ? Math.max(...vendor.stamp_rewards.map(s => s.orders_required)) : 10}` : "0"} disabled className="h-8 text-xs bg-surface" />
                    </div>
                  </div>
                </div>

                {/* Card Designer */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-ink/80">Card layout</Label>
                  <CardDesigner
                    vendor={vendor}
                    vendorId={id}
                    brandColor={brandColor}
                    textColor={textColor}
                    onLayoutChange={setCardLayout}
                  />
                </div>

                {/* Preview */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-ink/80">Preview</Label>
                  <div className="rounded-xl p-5 relative overflow-hidden" style={{ backgroundColor: brandColor, color: textColor }} data-testid="branding-preview">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {vendor.logo_icon_path && (
                          <img src={`${API}/vendors/${id}/logo/icon`} alt="" className="h-8 w-8 rounded-lg object-cover" />
                        )}
                        <span className="text-sm font-bold" style={{ color: textColor }}>{vendor.name}</span>
                      </div>
                      <span className="text-xs font-medium opacity-80" style={{ color: bodyTextColor }}>
                        {vendor.reward_logic === "order_based" ? "Stamps" : "Points"}
                      </span>
                    </div>
                    <p className="text-xs opacity-60" style={{ color: bodyTextColor }}>
                      {vendor.reward_logic === "order_based" ? "Collect stamps to earn rewards" : "Earn points on every purchase"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-5 flex justify-between items-center">
            <Button
              onClick={() => navigate(`/vendors/${id}/wallet-designer`)}
              variant="outline"
              className="gap-1.5 text-purple-500 border-purple-200 hover:bg-purple-50"
              data-testid="open-wallet-designer-btn"
            >
              <Pen size={13} /> Wallet Pass Designer
            </Button>
            <Button onClick={handleBrandingSave} disabled={brandingSaving} className="bg-purple-500 hover:bg-purple-600 text-white gap-1.5" data-testid="save-branding-btn">
              {brandingSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Branding
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="mt-4">
          <div className="bg-white rounded-md border border-ink/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-surface">
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Customer</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Balance</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Profile</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {customers.length === 0
                  ? <TableRow><TableCell colSpan={3} className="text-center py-8 text-ink/40 text-sm">No customers yet</TableCell></TableRow>
                  : customers.map(c => (
                    <TableRow key={c.id} className="cursor-pointer hover:bg-surface" onClick={() => navigate(`/customers/${c.id}`)}>
                      <TableCell><p className="text-sm font-medium">{c.name}</p><p className="text-xs text-ink/40">{c.email || c.mobile_number}</p></TableCell>
                      <TableCell className="font-mono text-sm">{c.point_balance?.toLocaleString()} pts</TableCell>
                      <TableCell>
                        {c.is_shadow_profile
                          ? <Badge variant="outline" className="text-xs bg-surface">Shadow</Badge>
                          : <Badge variant="outline" className="text-xs bg-purple-100 text-success border-success/30">Verified</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <div className="bg-white rounded-md border border-ink/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-surface">
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Tx ID</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Store</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Points</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Time</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {transactions.length === 0
                  ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-ink/40 text-sm">No transactions yet</TableCell></TableRow>
                  : transactions.map(t => (
                    <TableRow key={t.id}>
                      <TableCell className="font-mono text-xs text-ink/40">{t.tx_id?.slice(0, 8)}...</TableCell>
                      <TableCell className="text-sm">{t.store_id}</TableCell>
                      <TableCell className="font-mono text-sm">£{t.total_amount_gbp?.toFixed(2)}</TableCell>
                      <TableCell className="font-mono text-sm text-ink">+{t.points_earned}</TableCell>
                      <TableCell className="text-xs text-ink/40">{t.timestamp?.slice(0, 16).replace("T", " ")}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="api-logs" className="mt-4" data-testid="api-logs-tab-content">
          <div className="bg-white rounded-md border border-ink/10 overflow-hidden">
            <div className="px-5 py-3 border-b border-ink/5 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-ink">API Call Log</p>
                <p className="text-xs text-ink/40">{apiLogsTotal} total calls recorded</p>
              </div>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => loadApiLogs(1)} data-testid="refresh-api-logs">
                <RefreshCw size={12} /> Refresh
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-surface">
                  <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Timestamp</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Endpoint</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Identifier</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Store</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Points</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiLogsLoading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-ink/40 text-sm"><Loader2 size={16} className="animate-spin inline mr-2" />Loading...</TableCell></TableRow>
                ) : apiLogs.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-ink/40 text-sm">No API calls logged yet.</TableCell></TableRow>
                ) : (
                  apiLogs.map(log => (
                    <TableRow key={log.id} data-testid={`api-log-row-${log.id}`}>
                      <TableCell className="font-mono text-xs text-ink/40 whitespace-nowrap">{log.timestamp?.slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell><Badge variant="outline" className={`text-xs ${ENDPOINT_COLORS[log.endpoint] || "bg-surface text-ink/60"}`}>{log.endpoint}</Badge></TableCell>
                      <TableCell className="font-mono text-xs text-ink/60 max-w-36 truncate">{log.identifier}</TableCell>
                      <TableCell className="text-xs font-mono text-ink/50">{log.store_id || "\u2014"}</TableCell>
                      <TableCell className="font-mono text-xs">{log.total_amount_gbp != null ? `£${Number(log.total_amount_gbp).toFixed(2)}` : "\u2014"}</TableCell>
                      <TableCell className="font-mono text-xs text-ink">{log.points_earned != null && log.points_earned > 0 ? `+${log.points_earned}` : "\u2014"}</TableCell>
                      <TableCell><Badge variant="outline" className={`text-xs ${log.status === "success" ? "bg-purple-100 text-success border-success/30" : "bg-danger/10 text-danger border-danger/30"}`}>{log.status}</Badge></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {apiLogsTotalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-ink/5 text-sm text-ink/50">
                <span>Page {apiLogsPage} of {apiLogsTotalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={apiLogsPage === 1} onClick={() => loadApiLogs(apiLogsPage - 1)} className="h-7 text-xs"><ChevronLeft size={13} /></Button>
                  <Button variant="outline" size="sm" disabled={apiLogsPage === apiLogsTotalPages} onClick={() => loadApiLogs(apiLogsPage + 1)} className="h-7 text-xs"><ChevronRight size={13} /></Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
